import { useCallback, useState } from "react";
import type {
  FieldValues,
  Path,
  SubmitHandler,
  UseFormReturn,
} from "react-hook-form";
import { applyServerFieldErrors, authErrorMessage } from "./auth.errors";

export interface UseAuthSubmitOptions<
  TValues extends FieldValues,
  TSubmitted extends FieldValues,
  TContext,
> {
  /** El formulario, para pintar en su campo lo que el servidor señale. */
  form: UseFormReturn<TValues, TContext, TSubmitted>;
  /**
   * Campos que existen en ESTE formulario. Un campo que el servidor manda
   * pero el formulario no tiene cae en el aviso general: pintarlo en la
   * nada lo dejaría invisible.
   */
  fields: readonly Path<TValues>[];
  /** La llamada al API. Recibe los valores ya validados. */
  action: (values: TSubmitted) => Promise<unknown>;
  /** Qué hacer cuando salió bien — casi siempre, navegar. */
  onSuccess: () => void;
  /**
   * Códigos que pertenecen a un campo aunque el servidor no mande `fields`.
   * El registro pasa `fieldForErrorCode` (un `EMAIL_TAKEN` sin `fields`
   * sigue siendo un problema del correo); el login no lo necesita y por eso
   * es opcional, en vez de obligar a las dos pantallas a conocer el caso de
   * una.
   */
  fieldForCode?: (error: unknown) => Path<TValues> | null;
  /**
   * La última palabra de la PANTALLA sobre un fallo. Devuelve `true` si ya
   * se hizo cargo, y entonces no se pinta el aviso genérico.
   *
   * Existe por dos casos reales donde el banner de arriba es la respuesta
   * equivocada, no una respuesta pobre:
   *
   *  - El login ante `EMAIL_NOT_VERIFIED`: el fallo tiene una SALIDA
   *    concreta (reenviar el enlace), y enseñarla es infinitamente mejor
   *    que una frase que deja a alguien reintentando una contraseña que
   *    estaba bien.
   *  - `/restablecer` ante un token que el servidor rechaza: seguir
   *    enseñando el formulario invita a escribir la contraseña otra vez
   *    para que falle igual. La pantalla cambia de estado entera.
   *
   * Se consulta DESPUÉS de los errores por campo: un fallo que el servidor
   * ya señaló sobre un campo pertenece a ese campo, no a la pantalla.
   */
  onError?: (error: unknown) => boolean;
}

export interface AuthSubmit<TSubmitted extends FieldValues> {
  /** Va directo a `<Form onSubmit={...}>`. */
  submit: SubmitHandler<TSubmitted>;
  /** Lo que no aterrizó en ningún campo. `null` cuando no hay nada. */
  formError: string | null;
  dismissFormError: () => void;
}

/**
 * El baile de enviar un formulario de acceso, una sola vez.
 *
 * Login y registro repetían la misma coreografía completa: limpiar el aviso
 * anterior, llamar al API, navegar si salió bien y, si no, repartir el
 * fallo — primero a los campos que el servidor señaló, luego al campo que
 * el CÓDIGO implica, y solo si nada aterrizó, al aviso de arriba.
 *
 * El orden importa y es la razón de que esto sea un hook y no dos copias:
 * si el aviso genérico se pintara siempre, un "ese correo ya existe" saldría
 * DOS veces —en el campo y arriba— y quien lo lee tendría que adivinar si
 * son un problema o dos.
 */
export function useAuthSubmit<
  TValues extends FieldValues,
  TSubmitted extends FieldValues = TValues,
  TContext = unknown,
>({
  form,
  fields,
  action,
  onSuccess,
  fieldForCode,
  onError,
}: UseAuthSubmitOptions<TValues, TSubmitted, TContext>): AuthSubmit<TSubmitted> {
  const [formError, setFormError] = useState<string | null>(null);
  const dismissFormError = useCallback(() => setFormError(null), []);

  const submit: SubmitHandler<TSubmitted> = async (values) => {
    setFormError(null);

    try {
      await action(values);
      onSuccess();
    } catch (error) {
      // 1. Lo que el servidor señaló por campo, a su campo.
      if (applyServerFieldErrors(error, form.setError, fields)) return;

      const message = authErrorMessage(error);

      // 2. Lo que el código implica, aunque no venga `fields`.
      const field = fieldForCode?.(error) ?? null;
      if (field) {
        form.setError(field, { type: "server", message });
        return;
      }

      // 3. Lo que la pantalla sabe pintar mejor que un banner.
      if (onError?.(error)) return;

      // 4. Lo que no es de nadie en particular, arriba.
      setFormError(message);
    }
  };

  return { submit, formError, dismissFormError };
}
